import useClassNames from '@medflex/hooks/useClassNames';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import matchesSearchTerm from '@medflex/utils/matchesSearchTerm';
import { IconSearch } from './Icons';
import InputField from './InputField';
import UserInfo from './UserInfo';
import SearchHighlight from './SearchHighlight';
import Spinner from './Spinner';
import WindowingContainer from './WindowingContainer';

const getMatchedContacts = (searchTerm, contacts) => {
  if (searchTerm && searchTerm.trim().length) {
    return contacts.filter(contact => matchesSearchTerm(searchTerm, contact.fullNameWithTitle));
  }
  return contacts;
};

const ContactSearchField = ({ onSubmit, contacts, className }) => {
  const { register, reset: resetForm, formState, watch = () => {} } = useForm();
  const { t } = useTranslation('components');
  const cn = useClassNames(() => ['relative', className], [className]);
  const [isDirty, setIsDirty] = useState(false);
  const contactsListRef = useRef();
  const query = watch('query', '');

  const contactMatches = useMemo(() => getMatchedContacts(query, contacts), [query, contacts]);

  const reset = () => {
    setIsDirty(false);
    resetForm();
  };

  const handleOuterClick = event => {
    if (contactsListRef.current && !contactsListRef.current.contains(event.target) && !isDirty) {
      reset();
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleOuterClick);

    return () => {
      document.removeEventListener('click', handleOuterClick);
    };
  }, []);

  useEffect(() => {
    if (formState.isDirty) {
      setIsDirty(true);
    }
  }, [formState.isDirty]);

  const createSelectHandler = userId => e => {
    e.preventDefault();
    onSubmit(contacts.find(contact => contact.userId === userId));
    reset();
  };

  const handleKeyUp = e => {
    const resultList = e.currentTarget;

    if (e.key === 'Escape') {
      reset();
      return;
    }

    if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
      const matches = Array.from(resultList.querySelectorAll('li button'));
      const focusedElement = resultList.querySelector('li button:focus');

      if (e.key === 'ArrowDown') {
        if (focusedElement) {
          const next = focusedElement.parentElement.nextSibling;
          if (next) {
            next.querySelector('button').focus();
            return;
          }
        }

        matches[0].focus();
      }

      if (e.key === 'ArrowUp') {
        if (focusedElement) {
          const previous = focusedElement.parentElement.previousSibling;
          if (previous) {
            previous.querySelector('button').focus();
            return;
          }
        }
        matches[matches.length - 1].focus();
      }
    }
  };

  return (
    /* eslint-disable-next-line */
    <form
      data-components="ContactSearchField"
      ref={contactsListRef}
      className={cn}
      onKeyUp={handleKeyUp}
      onFocus={() => setIsDirty(true)}
    >
      <InputField
        type="search"
        name="query"
        className="flex-shrink-0 flex-grow bg-white"
        autoComplete="off"
        defaultValue=""
        ref={register}
        placeholder={t('ContactSearchField.placeholder')}
        afterInput={
          <div className="pr-4 flex justiy-center items-center cursor-pointer">
            <IconSearch size="5" className="fill-current text-slate-500" />
          </div>
        }
      />
      <div className="absolute w-full rounded shadow-md overflow-hidden z-popup-menu">
        <WindowingContainer list={contactMatches}>
          {({ windowedList, ref, listWindowLimit }) => (
            <ul
              className="bg-white w-full shadow-md overflow-y-auto max-h-48"
              hidden={!isDirty}
              ref={ref}
            >
              {windowedList.map(contact => (
                <li className="flex items-start" key={contact.userId}>
                  <button
                    aria-label={contact.fullNameWithTitle}
                    onClick={createSelectHandler(contact.userId)}
                    type="submit"
                    className="flex items-start flex-auto p-3 hover:bg-slate-100 focus:bg-slate-100 cursor-pointer hover:text-blue-500 focus:text-blue-500 focus:outline-none"
                  >
                    <UserInfo
                      avatarSize="8"
                      avatarInitials={contact.initials}
                      avatarSrc={contact.avatar}
                      title={
                        <SearchHighlight text={contact.fullNameWithTitle} toHighlight={query} />
                      }
                    />
                  </button>
                </li>
              ))}
              {listWindowLimit < contactMatches.length && (
                <div className="mb-2 w-full text-center">
                  <Spinner size="lg" />
                </div>
              )}
            </ul>
          )}
        </WindowingContainer>
      </div>
    </form>
  );
};

ContactSearchField.propTypes = {
  onSubmit: PropTypes.func,
  contacts: PropTypes.arrayOf(
    PropTypes.shape({
      userId: PropTypes.string,
      fullNameWithTitle: PropTypes.string,
      avatar: PropTypes.string,
      initials: PropTypes.string,
    }),
  ),
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
};

ContactSearchField.defaultProps = {
  onSubmit: () => {},
  contacts: [],
};

export default ContactSearchField;
